import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Form,
  Select,
  Input,
  DatePicker,
  Button,
  Table,
  message,
  Space,
  Modal,
  Descriptions,
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  PrinterOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import moment from "moment";

import { setMenu } from "../../redux/reducers/navigationSlice";
import { setUserAccount } from "../../redux/reducers/userAccountSlice";
import AudioPlayer from "../../component/audio-player/audio-player.component";
import DataTotalDisplay from "../../component/data-total-display/data-total-display.component";
import FormButtonsWrap from "../../component/form-buttons-wrap/form-buttons-wrap.component";
import VisitAPI from "../../api/VisitAPI";
import SchedulesGroupAPI from "../../api/SchedulesGroupAPI";
import config from "../../frontend.config.json";

import "./visits-report.styles.css";

const initialFormValues = {
  visitor: "",
  documentId: "",
  filterDateType: "",
  startDate: "",
  endDate: "",
  plate: "",
  visitType: "",
  resident: "",
  building: "",
  createdBy: "",
  scheduleGroupId: "",
};

const resumeTableColumns = [
  {
    title: "Total Visitas",
    dataIndex: "visitsCount",
    defaultSortOrder: "descend",
  },
  {
    title: "Visitas Invitación",
    dataIndex: "visitsByCodeCount",
    defaultSortOrder: "descend",
  },
  {
    title: "Visitas Confirmación",
    dataIndex: "visitsByConfirmationCount",
    defaultSortOrder: "descend",
  },
  {
    title: "Visitas Tarjeta Acceso",
    dataIndex: "visitsByAccessCount",
    defaultSortOrder: "descend",
  },
  {
    title: "Total Invitaciones",
    dataIndex: "invitationsCount",
    defaultSortOrder: "descend",
  },
  {
    title: "Total Rechazos",
    dataIndex: "rejectsCount",
    defaultSortOrder: "descend",
  },
  {
    title: "Total Peatones",
    dataIndex: "personsAccessCount",
    defaultSortOrder: "descend",
  },
  {
    title: "Total Vehículos",
    dataIndex: "vehiclesAccessCount",
    defaultSortOrder: "descend",
  },
];

const VisitsReportPage = () => {
  const dispatch = useDispatch();
  const [messageApi, messageContext] = message.useMessage();
  const token = useSelector((state) => state.userAccount.user.token);
  const [searchForm] = Form.useForm();

  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [visitListData, setVisitListData] = useState({
    visits: [],
    totalCount: 0,
  });
  const [visitListResumeData, setVisitListResumeData] = useState([]);
  const [scheduleGroups, setScheduleGroups] = useState([]);
  const [tableIsLoading, setTableLoading] = useState(false);
  const [imageModalIsOpen, setImageModalIsOpen] = useState(false);
  const [selectedVisitFiles, setSelectedVisitFiles] = useState({
    Id: "",
    LicensePlatePicture: "",
    CamPicture1: "",
    CamPicture2: "",
    CamPicture3: "",
    CamPicture4: "",
    AudioRecord: "",
    IDPicture: "",
  });

  const tableColumn = [
    {
      title: "Visitante",
      dataIndex: "Visitor",
      defaultSortOrder: "descend",
    },
    {
      title: "Documento Visitante",
      dataIndex: "DocumentID",
      defaultSortOrder: "descend",
    },
    {
      title: "Grupo de Horario",
      dataIndex: "ScheduleGroupName",
    },
    {
      title: "Fecha Entrada",
      dataIndex: "StartDate",
    },
    {
      title: "Fecha Salida",
      dataIndex: "EndDate",
    },
    {
      title: "Placa",
      dataIndex: "LicensePlate",
    },
    { title: "Tipo Entrada", dataIndex: "VisitType" },
    {
      title: "Residente",
      dataIndex: "Resident",
    },
    {
      title: "Num. Residencia",
      dataIndex: "Building",
    },
    {
      title: "Usuario Seguridad",
      dataIndex: "CreatedBy",
    },
    {
      title: "Detalles",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => loadSelectedSessionFiles(record.key)}
          >
            Ver
          </Button>
        </Space>
      ),
    },
    {
      title: "Ver cámaras",
      render: (_, record) => {
        // Convertir la fecha de entrada a formato UNIX
        const unixTime =
          moment(record.StartDate, "DD/MM/YY HH:mm:ss").unix() * 1000; // Multiplicar por 1000 para obtener la milésima de segundos
        // Obtener valores de las variables de entorno
        const cam = config.REACT_APP_CAMERA_ID;
        const group = config.REACT_APP_GROUP_ID;
        const host = config.REACT_APP_HOST;
        const port = config.REACT_APP_PORT;
        // Crear el enlace
        const link = `http://${host}:${port}/index.html#group=${group}&cam=${cam}&pb=${unixTime}`;
        return (
          <a href={link} target="_blank" rel="noopener noreferrer">
            Ver cámaras
          </a>
        );
      },
    },
  ];

  const loadInitialData = useCallback(async () => {
    try {
      messageApi.open({ type: "loading", content: "Cargando..." });

      const response = await VisitAPI.getVisitList(token, {});

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      const resumeResponse = await VisitAPI.getVisitListResume(token);

      if (!resumeResponse.data.success) {
        messageApi.open({
          type: "error",
          content: resumeResponse.data.message,
        });
        return;
      }

      const scheduleGroupsResponse = await SchedulesGroupAPI.getSchedulesGroup(
        token
      );

      if (!scheduleGroupsResponse.data.success) {
        messageApi.open({
          type: "error",
          content: scheduleGroupsResponse.data.message,
        });
        return;
      }

      const visitListData = response.data;
      const visitListResumeData = resumeResponse.data;
      const scheduleGroupsData = scheduleGroupsResponse.data;

      setVisitListData(visitListData.data);
      setVisitListResumeData([{ key: 1, ...visitListResumeData.data }]);
      setScheduleGroups(scheduleGroupsData.schedulesGroup);
    } catch (err) {
      console.error(err.message);
      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    } finally {
      messageApi.destroy();
    }
  }, [dispatch, messageApi, token]);

  const loadVisitList = async (activePage = 1, activePageSize = 10) => {
    try {
      messageApi.open({ type: "loading", content: "Cargando..." });

      const startDate = searchForm.getFieldValue("startDate")
        ? moment(
            new Date(
              searchForm.getFieldValue("startDate")
            ).toLocaleDateString(),
            "DD/MM/YYYY"
          ).format("YYYY-MM-DD")
        : "";
      const endDate = searchForm.getFieldValue("endDate")
        ? moment(
            new Date(searchForm.getFieldValue("endDate")).toLocaleDateString(),
            "DD/MM/YYYY"
          ).format("YYYY-MM-DD")
        : "";
      const response = await VisitAPI.getVisitList(token, {
        page: activePage,
        pageSize: activePageSize,
        ...searchForm.getFieldsValue(),
        startDate,
        endDate,
      });

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      const resumeResponse = await VisitAPI.getVisitListResume(token, {
        ...searchForm.getFieldsValue(),
        startDate,
        endDate,
      });

      if (!resumeResponse.data.success) {
        messageApi.open({
          type: "error",
          content: resumeResponse.data.message,
        });
        return;
      }

      const visitListData = response.data;
      const visitListResumeData = resumeResponse.data;

      setCurrentPage(activePage);
      setVisitListData(visitListData.data);
      setVisitListResumeData([{ key: 1, ...visitListResumeData.data }]);
    } catch (err) {
      console.error(err.message);
      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    } finally {
      messageApi.destroy();
    }
  };

  const loadSelectedSessionFiles = async (id) => {
    setTableLoading(true);

    try {
      const filesResponse = await VisitAPI.getVisitFiles(token, id);

      if (!filesResponse.data.success) {
        messageApi.open({
          type: "error",
          content: filesResponse.data.message,
        });
        return;
      }

      setSelectedVisitFiles(filesResponse.data);
      setImageModalIsOpen(true);
    } catch (err) {
      console.error(err.message);
      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    dispatch(setMenu("visitsReport"));
    loadInitialData();
  }, [dispatch, loadInitialData]);

  const onClickDownloadButton = async () => {
    try {
      messageApi.open({ type: "loading", content: "Cargando..." });
      const startDate = searchForm.getFieldValue("startDate")
        ? moment(
            new Date(
              searchForm.getFieldValue("startDate")
            ).toLocaleDateString(),
            "DD/MM/YYYY"
          ).format("YYYY-MM-DD")
        : "";
      const endDate = searchForm.getFieldValue("endDate")
        ? moment(
            new Date(searchForm.getFieldValue("endDate")).toLocaleDateString(),
            "DD/MM/YYYY"
          ).format("YYYY-MM-DD")
        : "";
      const response = await VisitAPI.downloadVisitReport(token, {
        ...searchForm.getFieldsValue(),
        startDate,
        endDate,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Visitas.xlsx");
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error(err.message);
      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    } finally {
      messageApi.destroy();
    }
  };

  const onClickPrintButton = async () => {
    try {
      const startDate = searchForm.getFieldValue("startDate")
        ? moment(
            new Date(
              searchForm.getFieldValue("startDate")
            ).toLocaleDateString(),
            "DD/MM/YYYY"
          ).format("YYYY-MM-DD")
        : "";
      const endDate = searchForm.getFieldValue("endDate")
        ? moment(
            new Date(searchForm.getFieldValue("endDate")).toLocaleDateString(),
            "DD/MM/YYYY"
          ).format("YYYY-MM-DD")
        : "";
      const response = await VisitAPI.getHTMLVisitReport(token, {
        ...searchForm.getFieldsValue(),
        startDate,
        endDate,
      });

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      const htmlReport = response.data.html;
      const reportWindow = window.open("", "_blank");

      reportWindow.document.title = "Visualización de Reporte de Visitas";
      reportWindow.document.write(htmlReport);
    } catch (ex) {
      console.error(ex.message);
      let message = "Servicio no disponible, intente más tarde. ";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    }
  };

  return (
    <div className="visits-report">
      <>{messageContext}</>
      <Modal
        className="visits-files-model"
        title="Detalles"
        centered
        open={imageModalIsOpen}
        width={800}
        onCancel={() => setImageModalIsOpen(false)}
        footer={
          <Button type="primary" onClick={() => setImageModalIsOpen(false)}>
            Cerrar
          </Button>
        }
      >
        {selectedVisitFiles.AudioRecord ? (
          <AudioPlayer
            label="Grabación de llamada: "
            base64EncodedAudio={selectedVisitFiles.AudioRecord}
          />
        ) : null}
        {selectedVisitFiles.DoorOpeningExceptionId ? (
          <Descriptions
            title="Excepción"
            style={{ margin: "10px 0px" }}
            bordered
          >
            <Descriptions.Item label="Fecha" span={2}>
              {selectedVisitFiles.DoorOpeningExceptionDateCreated}
            </Descriptions.Item>
            <Descriptions.Item label="Usuario">
              {selectedVisitFiles.DoorOpeningExceptionUserName}
            </Descriptions.Item>
            <Descriptions.Item label="Razón" span={2}>
              {selectedVisitFiles.DoorOpeningExceptionReason}
            </Descriptions.Item>
            <Descriptions.Item label="Información">
              {selectedVisitFiles.DoorOpeningExceptionInfo}
            </Descriptions.Item>
          </Descriptions>
        ) : null}
        <div className="cam-files-container">
          <div>
            <p>Imagen de placa:</p>
            <img
              src={selectedVisitFiles.LicensePlatePicture}
              onError={(e) => {
                e.target.src = "/image_placeholder.png";
              }}
              alt="License plate"
            />
          </div>
          <div>
            <p>Imagen de documento de identidad:</p>
            <img
              src={selectedVisitFiles.IDPicture}
              onError={(e) => {
                e.target.src = "/image_placeholder.png";
              }}
              alt="ID Document"
            />
          </div>
        </div>
      </Modal>

      <Form
        size="small"
        className="visits-search-form"
        form={searchForm}
        initialValues={initialFormValues}
        onFinish={() => loadVisitList()}
      >
        <div>
          <Form.Item label="Visitante" name="visitor">
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item label="Documento visitante" name="documentId">
            <Input maxLength={20} placeholder="Búsqueda exacta" />
          </Form.Item>
          <Form.Item label="Grupo horario" name="scheduleGroupId">
            <Select>
              <Select.Option value="">-- Seleccionar --</Select.Option>
              {scheduleGroups.map((scheduleGroup) => (
                <Select.Option key={scheduleGroup.Id} value={scheduleGroup.Id}>
                  {scheduleGroup.Name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>
        <div>
          <Form.Item label="Placa" name="plate">
            <Input maxLength={20} />
          </Form.Item>
          <Form.Item label="Tipo de entrada" name="visitType">
            <Select>
              <Select.Option value="">-- Seleccionar --</Select.Option>
              <Select.Option value="Invitation">Invitación</Select.Option>
              <Select.Option value="NoInvitation">Sin Invitación</Select.Option>
              <Select.Option value="Resident">Residente</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="Residente" name="resident">
            <Input maxLength={50} />
          </Form.Item>
        </div>
        <div>
          <Form.Item label="1️⃣ Filtro de fecha" name="filterDateType">
            <Select>
              <Select.Option value="">-- Seleccionar --</Select.Option>
              <Select.Option value="entryDate">Fecha de entrada</Select.Option>
              <Select.Option value="exitDate">Fecha de salida</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="2️⃣ Rango inicio" name="startDate">
            <DatePicker format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item label="3️⃣ Rango fin" name="endDate">
            <DatePicker format="DD/MM/YYYY" />
          </Form.Item>
        </div>
        <div>
          <Form.Item label="Num. residencia" name="building">
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item label="Usuario seguridad" name="createdBy">
            <Input maxLength={64} />
          </Form.Item>
        </div>
        <div>
          <FormButtonsWrap vertical>
            <Button type="primary" htmlType="submit">
              <SearchOutlined /> Buscar
            </Button>
            <Button
              onClick={() => {
                searchForm.resetFields();
                searchForm.submit();
                setCurrentPage(1);
              }}
            >
              <ClearOutlined />
              Limpiar
            </Button>

            <Button onClick={onClickDownloadButton}>
              <DownloadOutlined />
              Descargar
            </Button>
            <Button onClick={onClickPrintButton}>
              <PrinterOutlined />
              Vista para impresión
            </Button>
          </FormButtonsWrap>
        </div>
      </Form>
      <Table
        size="small"
        columns={resumeTableColumns}
        dataSource={visitListResumeData}
        pagination={false}
      />
      <DataTotalDisplay
        currentCount={visitListData.visits.length}
        totalCount={visitListData.totalCount}
      />
      <Table
        columns={tableColumn}
        dataSource={visitListData.visits}
        loading={tableIsLoading}
        size="small"
        pagination={{
          current: currentPage,
          pageSize: currentPageSize,
          total: visitListData.totalCount,
        }}
        onChange={async (e) => {
          await loadVisitList(e.current, e.pageSize);
          setCurrentPage(e.current);
          setCurrentPageSize(e.pageSize);
        }}
        scroll={{ y: "330px" }}
        style={{ height: "260px" }}
      />
    </div>
  );
};

export default VisitsReportPage;
